import PropTypes from "prop-types";

/**
 * PropTypes for HeroSection component
 * @typedef {Object} HeroSectionProps
 * @property {string} [id] - Section ID for anchor navigation
 * @property {string} title - Hero title
 * @property {string} [subtitle] - Hero subtitle (HTML supported)
 * @property {string|Object} [image] - Dashboard image URL or media object
 * @property {string} buttonText - CTA button label
 * @property {string} buttonUrl - CTA button destination URL
 */
export const heroSectionPropTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  image: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      url: PropTypes.string,
      alternativeText: PropTypes.string,
    }),
  ]),
  buttonText: PropTypes.string.isRequired,
  buttonUrl: PropTypes.string.isRequired,
};

/**
 * PropTypes for BenefitSection component
 * @typedef {Object} BenefitSectionProps
 * @property {string} [id] - Section ID for anchor navigation
 * @property {string} title - Section title
 * @property {string} [subtitle] - Section subtitle
 * @property {('card-left'|'card-right')} layout - Layout variant
 * @property {Array<{title: string, description: string}>} [items] - Benefit items with checkmark icons
 */
export const benefitSectionPropTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  layout: PropTypes.oneOf(["card-left", "card-right"]).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    })
  ),
};

/**
 * PropTypes for CTASection component
 * @typedef {Object} CTASectionProps
 * @property {string} [id] - Section ID for anchor navigation
 * @property {string} title - CTA section title
 * @property {string} [description] - CTA description (richtext/HTML supported)
 * @property {string} buttonText - Button text
 * @property {string} buttonUrl - Button URL (internal or external)
 * @property {string|Object} [image] - CTA image URL or media object
 */
export const ctaSectionPropTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  buttonText: PropTypes.string.isRequired,
  buttonUrl: PropTypes.string.isRequired,
  image: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      url: PropTypes.string,
      alternativeText: PropTypes.string,
    }),
  ]),
};

/**
 * PropTypes for FAQSection component
 * @typedef {Object} FAQSectionProps
 * @property {string} [id] - Section ID for anchor navigation
 * @property {string} title - FAQ section title
 * @property {Array<{question: string, answer: string}>} items - FAQ items array
 */
export const faqSectionPropTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string.isRequired,
      answer: PropTypes.string.isRequired,
    })
  ).isRequired,
};

/**
 * PropTypes for HowItWorksSection component
 * @typedef {Object} HowItWorksSectionProps
 * @property {string} [id] - Section ID for anchor navigation
 * @property {string} [eyebrow] - Optional eyebrow/kicker text above the title
 * @property {string} title - Section title
 * @property {Array<{id?: string|number, step_label?: string, title: string, description?: string, icon?: Object|string}>} steps - Timeline steps
 */
export const howItWorksSectionPropTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      step_label: PropTypes.string,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      icon: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          url: PropTypes.string,
          alternativeText: PropTypes.string,
        }),
      ]),
    })
  ).isRequired,
};

/**
 * PropTypes for AudienceBenefitsSection component
 * @typedef {Object} AudienceBenefitsSectionProps
 * @property {string} [id] - Section ID for anchor navigation
 * @property {string} sectionTitle - Section heading
 * @property {string} [sectionSubtitle] - Section subheading/lede text
 * @property {Array<{title: string, subtitle?: string, layout_type: 'card-left'|'card-right', benefit_items: Array<{title: string, description: string}>}>} audiences - Audience-specific benefit blocks
 */
export const audienceBenefitsSectionPropTypes = {
  id: PropTypes.string,
  sectionTitle: PropTypes.string.isRequired,
  sectionSubtitle: PropTypes.string,
  audiences: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
      layout_type: PropTypes.oneOf(["card-left", "card-right"]).isRequired,
      benefit_items: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          description: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
};
